/**
 * usePortfolios Hook - Phase 2C-2
 * 
 * Fetches user's portfolios (GM mode).
 * Uses PortfolioService pattern.
 */

import { useState, useEffect, useCallback } from 'react';
import { Portfolio } from '../types';
import { PortfolioService, ApiError } from '../api';

interface UsePortfoliosResult {
  portfolios: Portfolio[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePortfolios(userId: string, poolId?: string): UsePortfoliosResult {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPortfolios = useCallback(async () => {
    if (!userId || userId === 'guest') {
      setPortfolios([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await PortfolioService.getUserPortfolios(userId, poolId);
      setPortfolios(data);
    } catch (err) {
      // 404 means feature not available yet
      if (err instanceof ApiError && err.status === 404) {
        setPortfolios([]);
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to load portfolios');
      }
    } finally {
      setLoading(false);
    }
  }, [userId, poolId]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  return { portfolios, loading, error, refetch: fetchPortfolios };
}
